import React from "react";

/**
 * 🛡️ SafeSection — a lightweight React Error Boundary for individual page
 * sections (Owner Audit, Friends, Phoenix Hatch, etc.).
 *
 * When a child render throws, the rest of the parent page CONTINUES to render.
 * Without this, ONE broken component can blank the entire Profile page
 * (the "black screen" symptom owner has been hitting on production).
 *
 * Usage:
 *   <SafeSection label="Owner Audit">
 *     <OwnerAuditPanel />
 *   </SafeSection>
 */
export default class SafeSection extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }
  static getDerivedStateFromError(err) {
    return { err };
  }
  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error(`[SafeSection:${this.props.label || "?"}]`, err, info);
  }
  reset = () => this.setState({ err: null });
  render() {
    if (!this.state.err) return this.props.children;
    const label = this.props.label || "This section";
    return (
      <div
        data-testid={`safe-section-crash-${(this.props.label || "unknown").toLowerCase().replace(/\s+/g, "-")}`}
        className="my-4 rounded-xl border border-amber-800/50 bg-amber-950/25 p-4 text-sm"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-amber-200 font-bold">
              ⚠️ {label} couldn&apos;t load
            </div>
            <div className="text-[11px] text-amber-300/70 mt-0.5 break-words">
              {String(this.state.err?.message || this.state.err).slice(0, 220)}
            </div>
            <div className="text-[10px] text-neutral-500 mt-1">
              The rest of the page is fine — the owner has been notified.
            </div>
          </div>
          <button
            onClick={this.reset}
            className="px-3 py-1.5 text-[11px] rounded-md bg-amber-700/40 hover:bg-amber-600/60 text-amber-100 font-bold uppercase tracking-widest"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
}
